import React, { useState } from "react";
import { EditorState, convertToRaw } from "draft-js";
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "draft-js/dist/Draft.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function AddBlogForm({ onSuccess }) {
  const [form, setForm] = useState({
    title: "",
    category: "",
    body: EditorState.createEmpty(),
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEditorChange = (editorState) => {
    setForm({ ...form, body: editorState });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setThumbnail(file);
    } else {
      setError("Please select a valid image file for thumbnail.");
      setThumbnail(null);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files).filter((file) => file.type.startsWith("image/"));
    if (files.length > 0) {
      setImages(files);
    } else {
      setError("Please select valid image files.");
      setImages([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!localStorage.getItem("access_token")) {
      setError("You must be logged in to add a blog.");
      setLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("category", form.category);
      if (thumbnail) formData.append("thumbnail", thumbnail);
      images.forEach((img) => formData.append("image_files", img));
      const contentState = form.body.getCurrentContent();
      const body = JSON.stringify(convertToRaw(contentState));
      if (body.trim() !== "") {
        formData.append("body", body);
      } else {
        setError("Blog body cannot be empty.");
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/blogs/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(JSON.stringify(errorData) || "Failed to add blog");
      }
      setForm({ title: "", category: "", body: EditorState.createEmpty() });
      setThumbnail(null);
      setImages([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: "auto", padding: 16 }}>
      <h2>Add Blog</h2>
      <input
        name="title"
        type="text"
        placeholder="Blog Title"
        value={form.title}
        onChange={handleChange}
        required
        className="input"
      />
      <input
        name="category"
        type="text"
        placeholder="Category"
        value={form.category}
        onChange={handleChange}
        required
        className="input"
      />
      <div style={{ marginTop: 8 }}>
        <label>
          Thumbnail: <input type="file" accept="image/*" onChange={handleThumbnailChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>
          Images: <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
        </label>
      </div>
      <div style={{ marginTop: 8 }}>
        <label>Body:</label>
        <div style={{ border: "1px solid #ccc", padding: 8, marginTop: 8, height: "300px", overflowY: "auto" }}>
          <WysiwygEditor
            editorState={form.body}
            onEditorStateChange={handleEditorChange}
            toolbar={{
              options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
              inline: { inDropdown: true, options: ['bold', 'italic', 'underline', 'monospace'] },
              blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'] },
              list: { inDropdown: true, options: ['unordered', 'ordered'] },
              textAlign: { inDropdown: true, options: ['left', 'center', 'right'] },
              link: { inDropdown: true },
              image: { inDropdown: true }
            }}
          />
        </div>
      </div>
      {error && <div style={{ color: "red", marginTop: 12 }}>{error}</div>}
      <button
        type="submit"
        className="btn-primary-primary"
        disabled={loading}
        style={{ marginTop: 12 }}
      >
        {loading ? "Saving..." : "Add Blog"}
      </button>
    </form>
  );
}

export default AddBlogForm;
